/**
 * Essence Customer API hooks — wraps the secured /api/essence/customers/ endpoints
 * with React Query mutations and queries.
 *
 * These replace the generated customer-management.ts hooks that pointed to the
 * unsecured /api/customer-management/ endpoints.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  UseMutationOptions,
  UseQueryOptions,
  QueryClient,
} from '@tanstack/react-query';
import type {
  CustomerDetailDTO,
  CustomerSearchCriteriaDTO,
  CustomerUpdateDTO,
  GroupMemberDTO,
  PageCustomerDTO,
} from '@/shared/api/model';
import { customInstance } from '@/shared/api/mutator/custom-instance';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProductWithBalance {
  glNo: string;
  productName: string;
  productType: string;
  balance?: number;
}

export interface GroupMemberAssignmentPayload {
  memberBranch?: string;
  memberCustNo: string;
  isLeader?: boolean;
  memberTemplateId?: number | null;
}

export interface CustomerDocumentPayload {
  documentType: string;
  documentTypeName?: string;
  fileId?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  mimeType?: string;
  notes?: string;
}

export interface CustomerDocumentResponse extends CustomerDocumentPayload {
  branch: string;
  custNo: string;
  uploadedAt?: string;
  uploadedBy?: string;
  isRequired?: boolean;
}

// ── Search ───────────────────────────────────────────────────────────────────

export const essenceSearchCustomers = (data: CustomerSearchCriteriaDTO) =>
  customInstance<PageCustomerDTO>({
    url: '/api/essence/customers/search',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  });

export const useEssenceSearchCustomers = (
  options?: {
    mutation?: UseMutationOptions<
      PageCustomerDTO,
      unknown,
      { data: CustomerSearchCriteriaDTO }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({ data }: { data: CustomerSearchCriteriaDTO }) =>
    essenceSearchCustomers(data);
  return useMutation(
    { mutationFn, mutationKey: ['essenceSearchCustomers'], ...options?.mutation },
    queryClient,
  );
};

// ── Customer Details ─────────────────────────────────────────────────────────

export const essenceGetCustomerDetails = (branch: string, custNo: string) =>
  customInstance<CustomerDetailDTO>({
    url: `/api/essence/customers/${branch}/${custNo}`,
    method: 'GET',
  });

export const useEssenceGetCustomerDetails = (
  branch: string,
  custNo: string,
  options?: { query?: Partial<UseQueryOptions<CustomerDetailDTO>> },
  queryClient?: QueryClient,
) =>
  useQuery(
    {
      queryKey: [`/api/essence/customers/${branch}/${custNo}`],
      queryFn: () => essenceGetCustomerDetails(branch, custNo),
      enabled: !!(branch && custNo),
      ...options?.query,
    },
    queryClient,
  );

// ── Enriched Details (with product balances) ─────────────────────────────────

export const essenceGetEnrichedDetails = (branch: string, custNo: string) =>
  customInstance<CustomerDetailDTO>({
    url: `/api/essence/customers/${branch}/${custNo}/enriched`,
    method: 'GET',
  });

export const useEssenceGetEnrichedDetails = (
  branch: string,
  custNo: string,
  options?: { query?: Partial<UseQueryOptions<CustomerDetailDTO>> },
  queryClient?: QueryClient,
) =>
  useQuery(
    {
      queryKey: [`/api/essence/customers/${branch}/${custNo}/enriched`],
      queryFn: () => essenceGetEnrichedDetails(branch, custNo),
      enabled: !!(branch && custNo),
      ...options?.query,
    },
    queryClient,
  );

// ── Products with Balances ───────────────────────────────────────────────────

export const essenceGetProductsWithBalances = (branch: string, custNo: string) =>
  customInstance<ProductWithBalance[]>({
    url: `/api/essence/customers/${branch}/${custNo}/products`,
    method: 'GET',
  });

export const useEssenceGetProductsWithBalances = (
  branch: string,
  custNo: string,
  options?: { query?: Partial<UseQueryOptions<ProductWithBalance[]>> },
  queryClient?: QueryClient,
) =>
  useQuery(
    {
      queryKey: [`/api/essence/customers/${branch}/${custNo}/products`],
      queryFn: () => essenceGetProductsWithBalances(branch, custNo),
      enabled: !!(branch && custNo),
      ...options?.query,
    },
    queryClient,
  );

// ── Update Customer ──────────────────────────────────────────────────────────

export const essenceUpdateCustomer = (data: CustomerUpdateDTO) =>
  customInstance<CustomerDetailDTO>({
    url: '/api/essence/customers/update',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data,
  });

export const useEssenceUpdateCustomer = (
  options?: {
    mutation?: UseMutationOptions<
      CustomerDetailDTO,
      unknown,
      { data: CustomerUpdateDTO }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({ data }: { data: CustomerUpdateDTO }) =>
    essenceUpdateCustomer(data);
  return useMutation(
    { mutationFn, mutationKey: ['essenceUpdateCustomer'], ...options?.mutation },
    queryClient,
  );
};

// ── Group Members ────────────────────────────────────────────────────────────

export const essenceAddGroupMember = (
  branch: string,
  custNo: string,
  data: GroupMemberAssignmentPayload,
) =>
  customInstance<CustomerDetailDTO>({
    url: `/api/essence/customers/${branch}/${custNo}/members`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  });

export const useEssenceAddGroupMember = (
  options?: {
    mutation?: UseMutationOptions<
      CustomerDetailDTO,
      unknown,
      { branch: string; custNo: string; data: GroupMemberAssignmentPayload }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({
    branch,
    custNo,
    data,
  }: {
    branch: string;
    custNo: string;
    data: GroupMemberAssignmentPayload;
  }) => essenceAddGroupMember(branch, custNo, data);

  return useMutation(
    { mutationFn, mutationKey: ['essenceAddGroupMember'], ...options?.mutation },
    queryClient,
  );
};

export const essenceCreateGroupMember = (
  branch: string,
  custNo: string,
  memberTemplateId: number,
  data: GroupMemberDTO,
) =>
  customInstance<CustomerDetailDTO>({
    url: `/api/essence/customers/${branch}/${custNo}/members/new`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    params: { memberTemplateId },
    data,
  });

export const useEssenceCreateGroupMember = (
  options?: {
    mutation?: UseMutationOptions<
      CustomerDetailDTO,
      unknown,
      { branch: string; custNo: string; memberTemplateId: number; data: GroupMemberDTO }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({
    branch,
    custNo,
    memberTemplateId,
    data,
  }: {
    branch: string;
    custNo: string;
    memberTemplateId: number;
    data: GroupMemberDTO;
  }) => essenceCreateGroupMember(branch, custNo, memberTemplateId, data);

  return useMutation(
    { mutationFn, mutationKey: ['essenceCreateGroupMember'], ...options?.mutation },
    queryClient,
  );
};

export const essenceRemoveGroupMember = (
  branch: string,
  custNo: string,
  memberCustNo: string,
) =>
  customInstance<CustomerDetailDTO>({
    url: `/api/essence/customers/${branch}/${custNo}/members/${memberCustNo}`,
    method: 'DELETE',
  });

export const useEssenceRemoveGroupMember = (
  options?: {
    mutation?: UseMutationOptions<
      CustomerDetailDTO,
      unknown,
      { branch: string; custNo: string; memberCustNo: string }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({
    branch,
    custNo,
    memberCustNo,
  }: {
    branch: string;
    custNo: string;
    memberCustNo: string;
  }) => essenceRemoveGroupMember(branch, custNo, memberCustNo);

  return useMutation(
    { mutationFn, mutationKey: ['essenceRemoveGroupMember'], ...options?.mutation },
    queryClient,
  );
};

// ── Documents ────────────────────────────────────────────────────────────────

export const essenceGetDocuments = (branch: string, custNo: string) =>
  customInstance<CustomerDocumentResponse[]>({
    url: `/api/essence/customers/${branch}/${custNo}/documents`,
    method: 'GET',
  });

export const useEssenceGetDocuments = (
  branch: string,
  custNo: string,
  options?: { query?: Partial<UseQueryOptions<CustomerDocumentResponse[]>> },
  queryClient?: QueryClient,
) =>
  useQuery(
    {
      queryKey: [`/api/essence/customers/${branch}/${custNo}/documents`],
      queryFn: () => essenceGetDocuments(branch, custNo),
      enabled: !!(branch && custNo),
      ...options?.query,
    },
    queryClient,
  );

export const essenceUploadDocument = (
  branch: string,
  custNo: string,
  data: CustomerDocumentPayload,
) =>
  customInstance<CustomerDocumentResponse>({
    url: `/api/essence/customers/${branch}/${custNo}/documents`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  });

export const useEssenceUploadDocument = (
  options?: {
    mutation?: UseMutationOptions<
      CustomerDocumentResponse,
      unknown,
      { branch: string; custNo: string; data: CustomerDocumentPayload }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({
    branch,
    custNo,
    data,
  }: {
    branch: string;
    custNo: string;
    data: CustomerDocumentPayload;
  }) => essenceUploadDocument(branch, custNo, data);
  return useMutation(
    { mutationFn, mutationKey: ['essenceUploadDocument'], ...options?.mutation },
    queryClient,
  );
};

export const essenceDeleteDocument = (
  branch: string,
  custNo: string,
  documentType: string,
) =>
  customInstance<void>({
    url: `/api/essence/customers/${branch}/${custNo}/documents/${documentType}`,
    method: 'DELETE',
  });

export const useEssenceDeleteDocument = (
  options?: {
    mutation?: UseMutationOptions<
      void,
      unknown,
      { branch: string; custNo: string; documentType: string }
    >;
  },
  queryClient?: QueryClient,
) => {
  const mutationFn = ({
    branch,
    custNo,
    documentType,
  }: {
    branch: string;
    custNo: string;
    documentType: string;
  }) => essenceDeleteDocument(branch, custNo, documentType);
  return useMutation(
    { mutationFn, mutationKey: ['essenceDeleteDocument'], ...options?.mutation },
    queryClient,
  );
};
